// index.js
import React from 'react';
import "./i18n";
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import App from './App';
import { CartProvider } from "./context/CartContext";
import { LocaleProvider } from "./context/LocaleContext";
import './index.css';
import PortraitLockWrapper from './components/layout/PortraitLockWrapper';
import { HelmetProvider } from 'react-helmet-async';
import { initializeNativeAuth } from './native/auth';
import { initializeNativeRegionRouting } from './native/regionRouting';
import { startRussianNativeNetworkRecovery } from './native/regionNetworkRecovery';
import { isNativeApp } from './native/runtime';
import { configureNativeKeyboard } from './native/keyboard';
import MobileUpdateController from './components/special/MobileUpdateController';
import PlatformNoticeController from './components/special/PlatformNoticeController';
import { useLocale } from './context/LocaleContext';

function removeLegacyServiceWorkers() {
  // Capacitor ships static assets inside the binary and never needs the old
  // web service worker cleanup. In particular, do not let a stale Android
  // WebView registration trigger a full-page reload on app startup/resume.
  if (isNativeApp()) return;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        if (!registrations.length) return Promise.resolve();

        return Promise.all(registrations.map((registration) => registration.unregister()))
          .then(() => {
            if (!window.caches?.keys) return undefined;
            return window.caches
              .keys()
              .then((keys) => Promise.all(keys.map((key) => window.caches.delete(key))));
          })
          .then(() => {
            if (navigator.serviceWorker.controller) {
              window.location.reload();
            }
          });
      })
      .catch((error) => {
        console.warn('Legacy service worker cleanup failed:', error);
      });
  });
}

function applyIOSFormZoomFixClass() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const { userAgent = '', platform = '', maxTouchPoints = 0 } = window.navigator || {};
  const isIOSDevice = /iPad|iPhone|iPod/i.test(userAgent);
  const isIPadOS = platform === 'MacIntel' && maxTouchPoints > 1;

  if (!isIOSDevice && !isIPadOS) return;

  document.documentElement.classList.add('ios-form-zoom-fix');
  if (document.body) {
    document.body.classList.add('ios-form-zoom-fix');
  }
}

removeLegacyServiceWorkers();
applyIOSFormZoomFixClass();
configureNativeKeyboard().catch((error) => {
  console.warn('Native keyboard configuration failed', error);
});

function RootWrapper() {
  const location = useLocation();
  const { language } = useLocale();
  const isCommunityLandingPath = /^\/(?:for-organizers|organizers|for-participants|participants)(?:\/(?:en|sk|cs))?\/?$/.test(location.pathname);
  // Full-bleed acquisition pages own their background and native document scroll.
  // They must not inherit the desktop platform content-width constraint.
  const isFullBleedAcquisitionPath =
    location.pathname === "/" ||
    location.pathname === "/about" ||
    location.pathname.startsWith("/about/") ||
    isCommunityLandingPath ||
    location.pathname === "/podujatia-bratislava" ||
    location.pathname === "/podujatia-bratislava/";

  // Keep the wrapper element stable across client-side navigation. Switching
  // between <App /> and <div><App /></div> remounts the whole app when users
  // leave the landing page, which is fragile in Safari during public catalog
  // bootstrap.
  return (
    <MobileUpdateController>
      <div className={isFullBleedAcquisitionPath ? undefined : "page-content"}>
        <App />
      </div>
      <PlatformNoticeController language={language} />
    </MobileUpdateController>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
const NATIVE_RENDER_DEADLINE_MS = 20_000;
const TELEGRAM_SDK_WAIT_MS = 4_000;
let appRendered = false;

function markBootStage(stage, detail = "") {
  window.__VG_NATIVE_BOOT__?.mark?.(stage, detail);
}

function renderApp(reason = "runtime_ready") {
  if (appRendered) return;
  appRendered = true;
  markBootStage("react_render", reason);
  // A development-only visual experiment, isolated from account/profile APIs.
  // Webpack removes this import (and all experiment assets) in production.
  if (
    process.env.NODE_ENV === 'development' &&
    !isNativeApp() &&
    ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname) &&
    ['/local-debug/castle-lab','/local-debug/castle-catalog'].includes(window.location.pathname.replace(/\/$/, ''))
  ) {
    const preview = window.location.pathname.replace(/\/$/, '') === '/local-debug/castle-catalog';
    const page = preview
      ? import('./experiments/castleLab/CastleCatalogPreview')
      : import('./experiments/castleLab/CastleLabPage');
    page.then(({ default: CastleStudyPage }) => {
      root.render(<CastleStudyPage />);
    }).catch((error) => {
      console.error('Castle experiment failed to load', error);
      document.getElementById('vg-boot-splash')?.remove();
      root.render(<p role="alert">Не удалось загрузить эксперимент. Обновите страницу.</p>);
    });
    return;
  }
  root.render(
    <HelmetProvider>
      <LocaleProvider>
        <CartProvider>
          <BrowserRouter>
            <PortraitLockWrapper>
              <RootWrapper />
            </PortraitLockWrapper>
          </BrowserRouter>
        </CartProvider>
      </LocaleProvider>
    </HelmetProvider>
  );
}

async function waitForTelegramRuntime() {
  if (isNativeApp() || window.__VG_TELEGRAM_SDK_REQUIRED__ !== true) return;
  const sdkPromise = window.__VG_TELEGRAM_SDK_PROMISE__;
  if (!sdkPromise || typeof sdkPromise.then !== "function") return;

  await Promise.race([
    sdkPromise,
    new Promise((resolve) => window.setTimeout(resolve, TELEGRAM_SDK_WAIT_MS)),
  ]);
}

async function initializeNativeRuntime() {
  if (!isNativeApp()) return;
  markBootStage("region_routing_started");
  await initializeNativeRegionRouting();
  markBootStage("region_routing_ready");
  markBootStage("native_auth_started");
  await initializeNativeAuth();
  markBootStage("native_auth_ready");

  // Network-change monitoring improves recovery after launch, but a plugin
  // listener must never be able to hold the first application render.
  void startRussianNativeNetworkRecovery()
    .then(() => markBootStage("network_monitor_ready"))
    .catch((error) => {
      markBootStage("network_monitor_failed", error?.name || "unknown_error");
      console.warn('Native network recovery setup failed', error);
    });
}

async function initializeApplicationRuntime() {
  await waitForTelegramRuntime();
  await initializeNativeRuntime();
}

markBootStage("main_bundle_loaded");
const nativeRenderDeadlineId = isNativeApp()
  ? window.setTimeout(() => renderApp("runtime_deadline"), NATIVE_RENDER_DEADLINE_MS)
  : null;

initializeApplicationRuntime()
  .catch((error) => {
    markBootStage("runtime_bootstrap_failed", error?.name || "unknown_error");
    console.warn('Native runtime bootstrap failed', error);
  })
  .finally(() => {
    if (nativeRenderDeadlineId != null) window.clearTimeout(nativeRenderDeadlineId);
    renderApp();
  });
